import React, {PropsWithChildren, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import QuestionReportGraph from '../../../components/graphs/QuestionReportGraph';
import {QuestionReport} from '../../../../incase-domain/analytics';
import SkeletonLoader from '../../../components/common/SkeletonLoader';
import Button from '../../../../incase-domain/components/button/Button';
import {getQuestionReportCsv} from './questionReportCsv';
import {downloadBlob} from '../../../../utils/fileUtils';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';

interface QuestionReportProps {
    questionReport: QuestionReport[];
    presentationName: string | undefined;
    slideToViewID: string | undefined;
    setSlideToViewID: (id: string | undefined) => void;
    isLoading: boolean;
    openAll?: boolean;
    disablePercentageWidth?: boolean;
}

const ALL_QUESTIONS = {id: 'all', name: 'all'};

function PresentationQuestions(props: PropsWithChildren<QuestionReportProps>): JSX.Element {
    const {questionReport, presentationName, slideToViewID, setSlideToViewID, isLoading, openAll, disablePercentageWidth} = props;
    const {t} = useTranslation();

    const [slideToViewIndex, setSlideToViewIndex] = useState<number>(0);
    const [slide, setSlide] = useState<{id: string; name: string}>({id: ALL_QUESTIONS.id, name: ALL_QUESTIONS.name});

    const chipNames = useMemo(() => {
        const res = [ALL_QUESTIONS];

        questionReport.forEach((x) => {
            const index = res.findIndex((val) => val.id === x.slide.id);
            if (index < 0) {
                res.push(x.slide);
            }
        });

        return res;
    }, [questionReport]);

    useEffect(() => {
        if (slideToViewID) {
            const index = chipNames.findIndex((val) => val.id === slideToViewID);
            if (index !== -1) {
                setSlideToViewIndex(index);
                document.querySelector('.presentationQuestions__header')?.scrollIntoView({behavior: 'smooth'});
            }
        }
    }, [slideToViewID, chipNames]);

    const filteredQuestion = useCallback(
        (index: number) => {
            if (chipNames[index].id === ALL_QUESTIONS.id) return questionReport;

            return questionReport?.filter((question) => question.slide.id === chipNames[index].id);
        },
        [questionReport, chipNames]
    );

    function downloadCsv(): void {
        const blob = new Blob(['\uFEFF', getQuestionReportCsv(questionReport)], {type: 'text/csv;charset=utf-8'});
        downloadBlob(blob, `${presentationName}-questionReport.csv`);
    }

    const chipItems = [
        chipNames.map((slide) => {
            if (slide.id === ALL_QUESTIONS.id)
                return {
                    label: t('reportPage.allQuestions'),
                    onClick: () => {
                        setSlide(slide);
                        setSlideToViewID(slide.id);
                        setSlideToViewIndex(chipNames.findIndex((value) => value.id === slide.id));
                    },
                };

            return {
                label: slide.name,
                onClick: () => {
                    setSlide(slide);
                    setSlideToViewID(slide.id);
                    setSlideToViewIndex(chipNames.findIndex((value) => value.id === slide.id));
                },
            };
        }),
    ];

    return (
        <div>
            <div className="u-flex u-h-between u-v-bottom">
                <div className="p-presReport__card_title">{t('reportPage.answers')}</div>
                <div className="u-flex u-v-center u-h-center u-gap-1">
                    <Button
                        data-report-pdf="hide"
                        leading="solar:download-minimalistic-linear"
                        variant="ghost"
                        className="c-sidepanel__dropdown_button"
                        fullWidth
                        disabled={!questionReport?.length}
                        onClick={downloadCsv}
                    >
                        {t('reportPage.slideQuestions.download')}
                    </Button>
                    <DropdownMenuCompact
                        className="c-sidepanel__dropdown"
                        items={chipItems}
                        menuPosition={DropdownMenuCompactMenuPosition.LEFT}
                    >
                        <Button className="c-sidepanel__dropdown_button" fullWidth trailing="solar:alt-arrow-down-linear" variant="ghost">
                            <>
                                {slide.id === ALL_QUESTIONS.id ? t('reportPage.allQuestions') : slide.name}:{' '}
                                {filteredQuestion(slideToViewIndex).length}{' '}
                                {filteredQuestion(slideToViewIndex).length === 1
                                    ? `${t('reportPage.slideQuestions.question')}`
                                    : `${t('reportPage.slideQuestions.questions')}`}
                            </>
                        </Button>
                    </DropdownMenuCompact>
                </div>
            </div>
            <div className="p-presReport__card">
                <SkeletonLoader isLoading={isLoading} props={{style: {minHeight: '30rem'}}}>
                    <div className="presentationQuestions">
                        <div className="presentationQuestions__footer">
                            {filteredQuestion(slideToViewIndex).map((question) => {
                                return (
                                    <QuestionReportGraph
                                        open={openAll}
                                        disableColorSlider={disablePercentageWidth}
                                        key={question.elementId}
                                        questionReport={question}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </SkeletonLoader>
            </div>
        </div>
    );
}

export default PresentationQuestions;
